import { Box, BottomNavigation, BottomNavigationAction, Badge, Paper } from '@mui/material';
import { IonIcon } from '@ionic/react';
import {
  homeOutline,
  home,
  chatbubbleEllipsesOutline,
  chatbubbleEllipses,
  addCircleOutline,
  addCircle,
  searchOutline,
  search,
  personOutline,
  person
} from 'ionicons/icons';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../hooks/useAuth';

export const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { user } = useAuth();
  const [unreadMessages, setUnreadMessages] = useState(0);
  
  const getIconForPath = (path: string, icon: string, activeIcon: string) => {
    return location.pathname === path ? activeIcon : icon;
  };

  // Hide navbar when a conversation is open
  const searchParams = new URLSearchParams(location.search);
  const isConversationOpen = location.pathname === '/messages' && searchParams.has('conversation');

  useEffect(() => {
    if (!user?.uid) return;

    // Listen for conversations with unread messages
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', user.uid)
    );

    const unsubscribe = onSnapshot(conversationsQuery, (snapshot) => {
      let totalUnread = 0;
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const unreadCount = data.unreadCount?.[user.uid] || 0;
        totalUnread += unreadCount;
      });
      
      setUnreadMessages(totalUnread);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  if (isConversationOpen) {
    return null;
  }

  return (
    <Box sx={{ 
      position: 'fixed', 
      bottom: 0, 
      left: 0, 
      right: 0, 
      zIndex: 1100,
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      pb: 1
    }}>
      <Paper
        elevation={3}
        sx={{
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(15px)',
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <BottomNavigation
          value={location.pathname}
          onChange={(_, newValue) => {
            history.push(newValue);
          }}
          showLabels
          sx={{
            height: '65px',
            background: 'transparent',
            '& .MuiBottomNavigationAction-root': {
              color: 'grey.500',
              '&.Mui-selected': {
                color: 'primary.main',
              },
              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            }
          }}
        >
        <BottomNavigationAction
          label={t('navbar.home')}
          value="/home"
          icon={
            <IonIcon 
              icon={getIconForPath('/home', homeOutline, home)} 
              style={{ fontSize: '24px' }}
              className={location.pathname === '/home' ? 'animate-pulse' : ''}
            />
          }
        />
        <BottomNavigationAction
          label={t('navbar.messages')}
          value="/messages"
          icon={
            <Badge 
              badgeContent={unreadMessages} 
              color="primary" 
              invisible={unreadMessages === 0}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.7rem',
                  height: '18px',
                  minWidth: '18px',
                  top: -2,
                  right: -2
                }
              }}
            >
              <IonIcon 
                icon={getIconForPath('/messages', chatbubbleEllipsesOutline, chatbubbleEllipses)} 
                style={{ fontSize: '24px' }}
                className={location.pathname === '/messages' ? 'animate-pulse' : ''}
              />
            </Badge>
          }
        />
        <BottomNavigationAction
          label={t('navbar.new_post')}
          value="/new-post"
          icon={
            <IonIcon 
              icon={getIconForPath('/new-post', addCircleOutline, addCircle)} 
              style={{ fontSize: '28px', color: location.pathname === '/new-post' ? '#0068B9' : undefined }}
              className={location.pathname === '/new-post' ? 'animate-pulse' : ''}
            />
          }
        />
        <BottomNavigationAction
          label={t('navbar.search')}
          value="/search"
          icon={
            <IonIcon 
              icon={getIconForPath('/search', searchOutline, search)} 
              style={{ fontSize: '24px' }}
              className={location.pathname === '/search' ? 'animate-pulse' : ''}
            />
          }
        />
        <BottomNavigationAction
          label={t('navbar.profile')}
          value="/my-profile"
          icon={
            <IonIcon 
              icon={getIconForPath('/my-profile', personOutline, person)} 
              style={{ fontSize: '24px' }}
              className={location.pathname === '/my-profile' ? 'animate-pulse' : ''}
            />
          }
        />
        </BottomNavigation>
      </Paper>
    </Box>
  );
};
